import { useEffect } from 'react';
import { Header } from '../components/Header';
import { FooterSection } from '../components/FooterSection';
import { Toaster } from '../components/ui/sonner';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Imprint() {
  const { lang } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isDE = lang === 'de';

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <div className="pt-28 pb-20 max-w-4xl mx-auto px-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#C9A84C] text-sm font-medium mb-8 hover:text-[#D4B85A] transition-colors duration-300"
        >
          <ArrowLeft size={16} />
          {isDE ? 'Zurück zur Startseite' : 'Back to Homepage'}
        </button>

        <h1 className="text-4xl sm:text-5xl font-bold text-[#F5F0E8] font-heading mb-4">
          {isDE ? 'Impressum' : 'Imprint'}
        </h1>
        <p className="text-[#8A8680] text-sm mb-12">
          {isDE ? 'Angaben gemäß § 5 TMG / Art. 10 LSSI-CE' : 'Information pursuant to § 5 TMG / Art. 10 LSSI-CE'}
        </p>

        <div className="space-y-10">
          {/* Company Info */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Angaben zum Unternehmen' : 'Company Information'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-2">
              <p className="text-[#C9A84C] font-semibold text-lg">Next Level</p>
              <p>{isDE ? 'Vertreten durch' : 'Represented by'}: Domenico Gurzi</p>
              <div className="mt-4 p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                <p>Calle Tres, Urb. Parque del Sol II</p>
                <p>Bloque Tres</p>
                <p>29670 Guadalmina Baja - Marbella</p>
                <p>{isDE ? 'Spanien' : 'Spain'}</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Kontakt' : 'Contact'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-2">
              <p>E-Mail: <a href="mailto:info@next-level.es" className="text-[#C9A84C] hover:text-[#D4B85A] transition-colors duration-300">info@next-level.es</a></p>
              <p>Web: <span className="text-[#F5F0E8]">www.next-level.es</span></p>
            </div>
          </div>

          {/* CEO */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Geschäftsführer' : 'Managing Director'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed">
              <p>Domenico Gurzi</p>
            </div>
          </div>

          {/* Tax / Registration */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Registrierung & Steuernummer' : 'Registration & Tax Number'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-2">
              <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                <p className="text-[#C9A84C] text-sm font-medium mb-2">
                  {isDE ? 'Wird nach Firmengründung ergänzt' : 'To be added upon company registration'}
                </p>
                <p>{isDE ? 'Handelsregister' : 'Commercial Register'}: TBD</p>
                <p>NIF / CIF: TBD</p>
                <p>{isDE ? 'USt-IdNr.' : 'VAT ID'}: TBD</p>
              </div>
            </div>
          </div>

          {/* Liability for Content */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Haftung für Inhalte' : 'Liability for Content'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-3">
              <p>
                {isDE
                  ? 'Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.'
                  : 'The contents of our pages have been created with the utmost care. However, we cannot guarantee the accuracy, completeness, or timeliness of the content. As a service provider, we are responsible for our own content on these pages in accordance with general laws. However, we are not obligated to monitor transmitted or stored third-party information.'}
              </p>
            </div>
          </div>

          {/* Liability for Links */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Haftung für Links' : 'Liability for Links'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-3">
              <p>
                {isDE
                  ? 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.'
                  : 'Our website contains links to external third-party websites over whose content we have no control. Therefore, we cannot accept any liability for this external content. The respective provider or operator of the linked pages is always responsible for the content of the linked pages.'}
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Urheberrecht' : 'Copyright'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-3">
              <p>
                {isDE
                  ? 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.'
                  : 'The content and works created by the site operators on these pages are subject to copyright law. Duplication, processing, distribution, and any form of commercialization of such material beyond the scope of copyright law requires the prior written consent of the respective author or creator.'}
              </p>
            </div>
          </div>

          {/* Dispute Resolution */}
          <div>
            <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
              {isDE ? 'Online-Streitbeilegung' : 'Online Dispute Resolution'}
            </h2>
            <div className="text-[#9A9590] text-base leading-relaxed space-y-3">
              <p>
                {isDE
                  ? 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/. Unsere E-Mail-Adresse finden Sie oben im Impressum. Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'
                  : 'The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr/. Our email address can be found above in the imprint. We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}
